import { useCallback, useEffect, useRef, useState } from 'react'

import { errorView } from '@krp/client-runtime'
import {
  COMMON_PHOTO_MAX_BYTES,
  cutoutBlob,
  newUuid,
  prepareStagingPhoto,
  previewStagingPhoto,
  sameBox,
  saveStagingPhoto,
  type AddPhotoVm,
  type CropBox,
  type PhotoCropReviewVm,
} from '@krp/platform-ui'

import { useAppDispatch } from '../../app/hooks'

interface Attempt {
  file: File
  abort: AbortController
  /** Вырез без фона от сервера: уходит обратно на предпросмотр и при сохранении. */
  cutout: Blob | null
  review: PhotoCropReviewVm | null
  /** Последняя сохраняемая рамка и её ключ операции: та же рамка — тот же ключ (потерянный ответ не даст второй загрузки), другая — новый. */
  save: { box: CropBox; operationId: string } | null
}

type ErrorOf = NonNullable<AddPhotoVm['error']>

function errorOf(error: unknown): ErrorOf {
  const view = errorView(error)
  return { kind: view.kind, code: view.code, message: view.message, correlationId: view.correlationId }
}

/**
 * «Добавить фото» в окне общего товара (RP-PSR-3, проект `PHOTO-SOURCES-REGISTRY-DESIGN.md` §4.1): файл → подготовка (вырез и предложенная рамка) → правка
 * рамки → предпросмотр результата → «Сохранить». До «Сохранить» на сервере ничего не лежит, поэтому «Отменить» ручки не вызывает. Файл и вырез живут здесь
 * (`File` и `Blob` в Redux класть нельзя). Отказ любого шага возвращает человека на тот же шаг с причиной дословно; сохранение той же рамки повторяется
 * с тем же ключом операции.
 */
export function useStagingPhotoAdd(
  productId: string,
  onSaved: () => void,
): {
  adding: AddPhotoVm | null
  start: (file: File) => void
  preview: (box: CropBox) => void
  edit: () => void
  save: () => void
  cancel: () => void
} {
  const dispatch = useAppDispatch()
  const [adding, setAdding] = useState<AddPhotoVm | null>(null)
  const attempt = useRef<Attempt | null>(null)
  const previewUrl = useRef<string | null>(null)

  const releasePreview = useCallback(() => {
    if (previewUrl.current !== null) URL.revokeObjectURL(previewUrl.current)
    previewUrl.current = null
  }, [])

  const release = useCallback(() => {
    if (attempt.current?.review != null) URL.revokeObjectURL(attempt.current.review.cutoutUrl)
    releasePreview()
    attempt.current = null
  }, [releasePreview])

  useEffect(
    () => () => {
      attempt.current?.abort.abort()
      release()
    },
    [release],
  )

  const start = useCallback(
    (file: File) => {
      attempt.current?.abort.abort()
      release()
      const current: Attempt = { file, abort: new AbortController(), cutout: null, review: null, save: null }
      attempt.current = current
      const base = { fileName: file.name, review: null, box: null, previewUrl: null } as const
      if (file.size > COMMON_PHOTO_MAX_BYTES) {
        setAdding({
          ...base,
          phase: 'failed',
          stage: '',
          error: { kind: 'client', code: 'photo-too-large', message: 'Фото больше 30 МиБ. Выберите другое.', correlationId: null },
        })
        return
      }
      setAdding({ ...base, phase: 'preparing', stage: 'Готовлю фото…', error: null })
      void dispatch(prepareStagingPhoto(productId, file, current.abort.signal, () => undefined)).then((outcome) => {
        if (current.abort.signal.aborted) return
        if (!outcome.ok) {
          setAdding({ ...base, phase: 'failed', stage: '', error: errorOf(outcome.error) })
          return
        }
        const data = outcome.data
        let blob: Blob
        try {
          blob = cutoutBlob(data.cutoutBase64, data.contentType)
        } catch {
          setAdding({
            ...base,
            phase: 'failed',
            stage: '',
            error: {
              kind: 'client',
              code: 'invalid-response',
              message: 'Сервер вернул фото без фона в неразборчивом виде. Повторите добавление; если повторится — сообщите администратору.',
              correlationId: null,
            },
          })
          return
        }
        const review: PhotoCropReviewVm = {
          cutoutUrl: URL.createObjectURL(blob),
          width: data.width,
          height: data.height,
          proposedBox: data.proposedBox,
          statusText: data.cropStatusText,
          margin: data.margin,
          maxSide: data.maxSide,
        }
        current.cutout = blob
        current.review = review
        setAdding({ ...base, phase: 'review', stage: '', review, error: null })
      })
    },
    [dispatch, productId, release],
  )

  const preview = useCallback(
    (box: CropBox) => {
      const current = attempt.current
      if (current === null || current.cutout === null || current.review === null) return
      const { cutout, review } = current
      current.abort = new AbortController()
      const { signal } = current.abort
      setAdding({ phase: 'previewing', fileName: current.file.name, stage: 'Строю предпросмотр…', review, box, previewUrl: null, error: null })
      void dispatch(previewStagingPhoto(productId, cutout, box, signal)).then((outcome) => {
        if (signal.aborted) return
        if (!outcome.ok) {
          setAdding({ phase: 'review', fileName: current.file.name, stage: '', review, box, previewUrl: null, error: errorOf(outcome.error) })
          return
        }
        releasePreview()
        previewUrl.current = URL.createObjectURL(outcome.data)
        setAdding({ phase: 'preview', fileName: current.file.name, stage: '', review, box, previewUrl: previewUrl.current, error: null })
      })
    },
    [dispatch, productId, releasePreview],
  )

  const edit = useCallback(() => {
    const current = attempt.current
    if (current === null || current.review === null) return
    setAdding((state) =>
      state === null ? null : { ...state, phase: 'review', stage: '', review: current.review, previewUrl: null, error: null },
    )
    releasePreview()
  }, [releasePreview])

  const save = useCallback(() => {
    const current = attempt.current
    const state = adding
    if (current === null || current.cutout === null || state === null || state.box === null) return
    const { box } = state
    const operationId = current.save !== null && sameBox(current.save.box, box) ? current.save.operationId : newUuid()
    current.save = { box, operationId }
    current.abort = new AbortController()
    const { signal } = current.abort
    setAdding({ ...state, phase: 'saving', stage: 'Сохраняю…', error: null })
    void dispatch(saveStagingPhoto(productId, current.cutout, current.file, box, operationId, signal)).then((outcome) => {
      if (signal.aborted) return
      if (!outcome.ok) {
        // Не сохранено: человек остаётся на предпросмотре с причиной; «Сохранить» повторит с тем же ключом операции.
        setAdding({ ...state, phase: 'preview', stage: '', error: errorOf(outcome.error) })
        return
      }
      release()
      setAdding(null)
      onSaved()
    })
  }, [adding, dispatch, onSaved, productId, release])

  const cancel = useCallback(() => {
    attempt.current?.abort.abort()
    release()
    setAdding(null)
  }, [release])

  return { adding, start, preview, edit, save, cancel }
}
